import type { Connector, ResourceRef, Workspace } from "@/types/api";

// A workspace stores its pipeline as the studio editor's JSON string, so the
// Workspaces list has to parse it back into a Connector before it can render
// the SRC → MW → SINK flow. The string is free-form editor content — partial
// JSON, plain text, anything — so anything that doesn't carry the fields the
// flow cell reads comes back as null (rendered as "—") instead of crashing
// the row.

function isStage(
  value: unknown,
): value is { service_name: string; resource: string } {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { service_name?: unknown }).service_name === "string" &&
    typeof (value as { resource?: unknown }).resource === "string"
  );
}

/** Lenient variant for the detail page's graph: whatever valid stages the
 *  stored document holds — a draft may be missing its sinks or carry
 *  malformed entries next to good ones, and the graph should still render
 *  the good ones (with unresolved markers where services are unknown).
 *  Null when nothing stage-shaped can be extracted at all. */
export function parseWorkspacePipelineLoose(
  pipeline: Workspace["pipeline"],
): Partial<Connector> | null {
  if (!pipeline) return null;

  let doc: unknown;
  try {
    doc = JSON.parse(pipeline);
  } catch {
    return null;
  }
  if (typeof doc !== "object" || doc === null) return null;

  const { source, middlewares, sinks } = doc as Record<string, unknown>;
  const src = isStage(source) ? source : undefined;
  const mws = Array.isArray(middlewares) ? middlewares.filter(isStage) : [];
  const snks = Array.isArray(sinks) ? sinks.filter(isStage) : [];
  if (!src && mws.length === 0 && snks.length === 0) return null;

  return {
    source: src,
    middlewares: mws,
    sinks: snks,
  } as Partial<Connector>;
}

/** The pipeline's transform scripts that actually resolve: the middleware's
 *  service is registered and, once that service's script list is known, the
 *  file exists in it. Unknown lookups (null sets / a service whose list
 *  hasn't loaded) count as resolved — same "can't verify → don't flag"
 *  fallback as the studio linter. */
export function resolvedPipelineScripts(
  connector: Partial<Connector> | null,
  serviceNames: Set<string> | null,
  scriptsByService: Map<string, Set<string>> | null,
): ResourceRef[] {
  if (!connector) return [];
  return (connector.middlewares ?? [])
    .filter((mw) => {
      if (serviceNames && !serviceNames.has(mw.service_name)) return false;
      const files = scriptsByService?.get(mw.service_name);
      return !files || files.has(mw.resource);
    })
    .map((mw) => ({
      service_name: mw.service_name,
      resource: mw.resource,
    }));
}

/** Registered services referenced by the pipeline's middlewares, for the
 *  per-script existence check (the script filename queries). Sorted and
 *  deduplicated so the array is a stable query key. */
export function registeredMiddlewareServices(
  pipeline: Partial<Connector> | null,
  serviceNames: Set<string> | null,
): string[] {
  const names = (pipeline?.middlewares ?? [])
    .map((mw) => mw.service_name)
    .filter((name) => !serviceNames || serviceNames.has(name));
  return [...new Set(names)].sort();
}

/** The workspace a deployed job came from: the one recording it as its
 *  deployed_job, a same-named one first when several claim it (a retired
 *  deployment can leave a stale deployed_job behind). */
export function workspaceForJob(
  workspaces: Workspace[] | undefined,
  jobName: string,
): Workspace | null {
  const claiming = (workspaces ?? []).filter(
    (ws) => ws.deployed_job === jobName,
  );
  return claiming.find((ws) => ws.name === jobName) ?? claiming[0] ?? null;
}

/** Mirrors the studio graph's unresolved flag: any stage whose service_name
 *  isn't a registered service. */
export function hasUnresolvedStage(
  connector: Connector,
  serviceNames: Set<string>,
): boolean {
  const stages = [
    connector.source,
    ...(connector.middlewares ?? []),
    ...connector.sinks,
  ];
  return stages.some((s) => !serviceNames.has(s.service_name));
}

export function parseWorkspacePipeline(
  pipeline: Workspace["pipeline"],
): Connector | null {
  if (!pipeline) return null;

  let doc: unknown;
  try {
    doc = JSON.parse(pipeline);
  } catch {
    return null;
  }
  if (typeof doc !== "object" || doc === null) return null;

  const { source, middlewares, sinks } = doc as Record<string, unknown>;
  if (!isStage(source)) return null;
  if (!Array.isArray(sinks) || !sinks.every(isStage)) return null;
  if (middlewares !== undefined) {
    if (!Array.isArray(middlewares) || !middlewares.every(isStage)) return null;
  }

  return doc as Connector;
}
